import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAttendanceByUsers,
  fetchEventLineup,
  fetchMutualVotersByArtist,
  fetchGroupMemberVotersByArtist,
  fetchMyArtistVotes,
  fetchMyEventAttendance,
} from '../../api/artistVotes';
import { festivalDaysFromLineup, voteKey } from '../../utils/lineupFormat';
import { queryKeys } from '../../lib/query/queryKeys';
import { STALE, GC } from '../../lib/query/staleTimes';

export function useEventLineup(eventId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.events.lineup(eventId),
    queryFn: () => fetchEventLineup(eventId),
    enabled: Boolean(eventId) && enabled,
    staleTime: STALE.semiStatic,
    gcTime: GC.festival, // offline at the festival: the lineup must survive a cold start (as the map does)
  });
}

export function useMyArtistVotes(eventId, userId) {
  return useQuery({
    queryKey: queryKeys.events.myArtistVotes(eventId, userId),
    queryFn: () => fetchMyArtistVotes(eventId, userId),
    enabled: Boolean(eventId && userId),
    staleTime: STALE.user,
  });
}

export function useMyEventAttendance(eventId, userId) {
  return useQuery({
    queryKey: queryKeys.events.myAttendance(eventId, userId),
    queryFn: () => fetchMyEventAttendance(eventId, userId),
    enabled: Boolean(eventId && userId),
    staleTime: STALE.user,
  });
}

/** Mutual follows who voted for each lineup artist (shared-interest panel). */
export function useLineupMutualVoters(eventId, userId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.events.lineupMutualVoters(eventId, userId),
    queryFn: () => fetchMutualVotersByArtist(eventId, userId),
    enabled: Boolean(eventId && userId) && enabled,
    staleTime: STALE.user,
  });
}

/**
 * The viewer's own picks, bucketed by festival day — backs the "My picks" hub.
 * Composes the lineup, votes, and attendance queries already in cache; no extra
 * network calls.
 */
export function useMyArtistVotesByDay(eventId, userId) {
  const lineupQuery = useEventLineup(eventId);
  const votesQuery = useMyArtistVotes(eventId, userId);
  const attendanceQuery = useMyEventAttendance(eventId, userId);

  const lineup = lineupQuery.data ?? [];
  const votes = votesQuery.data ?? [];
  const attendingDays = attendanceQuery.data ?? [];

  const days = useMemo(() => festivalDaysFromLineup(lineup), [lineup]);

  const byDay = useMemo(() => {
    const votedKeys = new Set(votes.map((v) => voteKey(v.artist_id, v.performance_day)));
    const attending = new Set(attendingDays);
    // Undated rows can't belong to a day bucket, so they ride along in every day's
    // total the same way LineupVoteList shows them regardless of the day filter.
    const undated = lineup.filter((row) => !row.performance_day);

    const buckets = days.map((day) => {
      const dayRows = lineup.filter((row) => row.performance_day === day);
      return {
        day,
        isAttending: attending.has(day),
        totalArtists: dayRows.length,
        picks: dayRows.filter((row) => votedKeys.has(voteKey(row.artist_id, day))),
      };
    });

    if (days.length === 0 && undated.length > 0) {
      buckets.push({
        day: null,
        isAttending: true,
        totalArtists: undated.length,
        picks: undated.filter((row) => votedKeys.has(voteKey(row.artist_id, null))),
      });
    }

    return buckets;
  }, [lineup, votes, attendingDays, days]);

  const totalPicks = byDay.reduce((sum, bucket) => sum + bucket.picks.length, 0);

  return {
    lineup,
    days,
    byDay,
    totalPicks,
    attendingDays,
    isLoading: lineupQuery.isLoading || votesQuery.isLoading || attendanceQuery.isLoading,
  };
}

/**
 * Attending days per member for one event — the day dots on the group Members page.
 * memberIds is folded into the query key so the cache tracks membership changes.
 */
export function useMemberAttendanceDays(eventId, memberIds = [], { enabled = true } = {}) {
  const sortedIds = [...memberIds].sort();

  return useQuery({
    queryKey: [...queryKeys.events.attendanceByUsers(eventId), ...sortedIds],
    queryFn: () => fetchAttendanceByUsers(eventId, sortedIds),
    enabled: Boolean(eventId) && sortedIds.length > 0 && enabled,
    staleTime: STALE.user,
  });
}

/** Group members who voted for each lineup artist (group lineup page). */
export function useGroupMemberVoters(
  groupId,
  eventId,
  members,
  blockedUsers,
  { enabled = true } = {},
) {
  const activeMembers = (members || []).filter((m) => !m.left_at && !blockedUsers?.has(m.user_id));
  const memberIds = activeMembers.map((m) => m.user_id);
  const profilesById = new Map(
    activeMembers.map((m) => [
      m.user_id,
      {
        id: m.user_id,
        display_name: m.profile?.display_name || 'User',
        profile_photo_url: m.profile?.profile_photo_url,
      },
    ]),
  );

  return useQuery({
    queryKey: [...queryKeys.chats.memberVoters(groupId, eventId), ...memberIds.sort()],
    queryFn: () => fetchGroupMemberVotersByArtist(eventId, memberIds, profilesById),
    enabled: Boolean(groupId && eventId && memberIds.length > 0) && enabled,
    staleTime: STALE.user,
  });
}
